// Mock Divergence Leaderboard field (sandbox-only).
//
// The real Leaderboard surfaces pledges from Supabase — in the sandbox
// there's typically a single Ghost citizen with a handful of pledges,
// which leaves the podium looking empty and the "Divergence" framing
// hard to read. This module seeds a competitive 10-row field so the
// board feels alive and the §2 RP / divergence narrative lands at first
// glance.
//
// Mock rows are deliberately kept in-memory and decoupled from any
// money-moving ledger. The Leaderboard component merges these with
// real Supabase rows at render time. When the production cut lands,
// this module's callers become the empty fallback path.

import { IdentityLevel, IdentityTier } from './identity';
import { calcDivergencePoints } from './divergence';

// ─── Types ──────────────────────────────────────────────────────────────

export interface MockLeaderRow {
  user_id:      string;       // stable id used for accent colour + React key
  alias:        string;       // display name
  tier:         IdentityTier; // CITIZEN (2) or SOVEREIGN (3) — no seekers on the podium
  dp:           number;       // Σ units × (1 − price) — canonical §2 compute
  rp:           number;       // Reputation Points (higher for Sovereigns at rest)
  accuracy:     number;       // win rate over last 20 signals, 0..100
  unitsTotal:   number;       // total units pledged (matches real Row shape)
  pledgeCount:  number;
  costTotal:    number;       // OC spent — units × avg price at pledge
}

// ─── Seed ───────────────────────────────────────────────────────────────
//
// Authored once at module load. DP values are plausible for a citizen
// who has pledged ~25–50 times against consensus; RP scales roughly
// with DP but carries a tier premium (Sovereigns have a longer history
// so their RP floor is higher even at comparable DP). Accuracy is a
// separate axis — a Sovereign contrarian with 77 % wins vs a Citizen
// at 49 % reads as different risk profiles on the same board.

type Seed = Omit<MockLeaderRow, 'user_id' | 'unitsTotal' | 'costTotal'> & {
  avgPrice: number; // used to backfill unitsTotal / costTotal consistently with DP
};

const SEEDS: Seed[] = [
  { alias: 'Sovereign_0x',     tier: IdentityLevel.SOVEREIGN, dp: 14280.44, rp: 5810, accuracy: 74, pledgeCount: 48, avgPrice: 0.29 },
  { alias: 'Kyoto_Oracle',     tier: IdentityLevel.CITIZEN,   dp: 12145.12, rp: 3420, accuracy: 68, pledgeCount: 52, avgPrice: 0.34 },
  { alias: 'London_Whale',     tier: IdentityLevel.SOVEREIGN, dp: 11890.60, rp: 4970, accuracy: 71, pledgeCount: 38, avgPrice: 0.31 },
  { alias: 'Signal_Seeker_99', tier: IdentityLevel.CITIZEN,   dp:  9620.80, rp: 2810, accuracy: 62, pledgeCount: 44, avgPrice: 0.38 },
  { alias: 'Berlin_Diviner',   tier: IdentityLevel.CITIZEN,   dp:  8340.25, rp: 2560, accuracy: 58, pledgeCount: 37, avgPrice: 0.42 },
  { alias: 'NYC_Contrarian',   tier: IdentityLevel.SOVEREIGN, dp:  7998.11, rp: 3940, accuracy: 77, pledgeCount: 29, avgPrice: 0.26 },
  { alias: 'Mumbai_Macro',     tier: IdentityLevel.CITIZEN,   dp:  6710.50, rp: 2330, accuracy: 54, pledgeCount: 33, avgPrice: 0.45 },
  { alias: 'Shenzhen_Flux',    tier: IdentityLevel.CITIZEN,   dp:  5480.90, rp: 1880, accuracy: 49, pledgeCount: 28, avgPrice: 0.48 },
  { alias: 'Edinburgh_Vex',    tier: IdentityLevel.CITIZEN,   dp:  4610.32, rp: 1640, accuracy: 52, pledgeCount: 26, avgPrice: 0.40 },
  { alias: 'Cairo_Signal',     tier: IdentityLevel.CITIZEN,   dp:  3290.74, rp: 1350, accuracy: 46, pledgeCount: 22, avgPrice: 0.36 },
];

function aliasToId(alias: string): string {
  return `mock_${alias.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
}

const ROWS: MockLeaderRow[] = SEEDS.map(seed => {
  // dp = unitsTotal × (1 − avgPrice), so unitsTotal = dp / (1 − avgPrice).
  // costTotal is what they paid: unitsTotal × avgPrice. Round to OC whole.
  const unitsTotal = seed.dp / (1 - seed.avgPrice);
  const costTotal  = unitsTotal * seed.avgPrice;
  return {
    user_id:     aliasToId(seed.alias),
    alias:       seed.alias,
    tier:        seed.tier,
    dp:          seed.dp,
    rp:          seed.rp,
    accuracy:    seed.accuracy,
    pledgeCount: seed.pledgeCount,
    unitsTotal:  Math.round(unitsTotal),
    costTotal:   Math.round(costTotal),
  };
});

// ─── Read API ───────────────────────────────────────────────────────────

export function listMockLeaders(): MockLeaderRow[] {
  // Sorted by DP desc per brief — "celebrating the contrarians".
  return ROWS.slice().sort((a, b) => b.dp - a.dp);
}

// ─── Churn (live-feel updates) ──────────────────────────────────────────
//
// Every 20–40 s the ticker picks one row and simulates a new contrarian
// pledge against consensus. DP grows via the canonical formula; RP
// trails DP at a §2-like fraction; accuracy drifts ±1–2 pp so it reads
// as evaluative rather than monotonic. Subscribers re-render on each
// tick so the podium glides without the user reloading.

type Listener = () => void;
const listeners: Set<Listener> = new Set();

const TICK_MS_MIN = 20_000;
const TICK_MS_MAX = 40_000;
let tickHandle: ReturnType<typeof setTimeout> | null = null;

function r(lo: number, hi: number): number { return lo + Math.random() * (hi - lo); }

function churnOnce(): void {
  const row = ROWS[Math.floor(Math.random() * ROWS.length)];
  // Simulate a pledge in the contrarian zone — price 0.12..0.48 is what
  // a Divergence citizen would actually bet into. A centred bet (price
  // near 0.5) would barely move DP, which would make the ticker feel
  // flat; skew the synthetic distribution toward where the signal
  // actually lives.
  const stake = Math.round(r(40, 180));
  const price = r(0.12, 0.48);
  const dpDelta = calcDivergencePoints(stake, price);
  row.dp          += dpDelta;
  row.unitsTotal  += stake;
  row.costTotal   += Math.round(stake * price);
  row.pledgeCount += 1;

  // RP nudge — 12–22 % of the DP delta rolls into RP for active
  // citizens. Matches the §2 scaling direction without committing to
  // exact coefficients (those live in the server-side reputation
  // engine when it lands).
  row.rp += Math.round(dpDelta * r(0.12, 0.22));

  // Accuracy drifts by ±1–2 pp, capped [35, 82]. The band is
  // deliberately narrower than [0, 100] — a row at 30 % accuracy
  // wouldn't stay on the podium, and a row at 95 % wouldn't be
  // believable as a contrarian specialist.
  const drift    = (Math.random() < 0.5 ? -1 : 1) * (Math.random() < 0.5 ? 1 : 2);
  row.accuracy   = Math.max(35, Math.min(82, row.accuracy + drift));

  listeners.forEach(fn => fn());
}

function scheduleNext(): void {
  const delay = r(TICK_MS_MIN, TICK_MS_MAX);
  tickHandle = setTimeout(() => { churnOnce(); scheduleNext(); }, delay);
}

function ensureTicker(): void {
  if (tickHandle !== null) return;
  if (typeof window === 'undefined') return; // SSR / test no-op
  scheduleNext();
}

export function subscribeMockLeaderboard(fn: Listener): () => void {
  listeners.add(fn);
  ensureTicker();
  return () => {
    listeners.delete(fn);
    // Leave the ticker running — the pool is module-level so a
    // component unmount shouldn't freeze the field. Consistent with
    // mockMarkets.ts and mintedMarkets.ts.
  };
}
